import { Controller } from "@hotwired/stimulus";

// Renders the "Seat & baggage" grid on the new-flight form: one row per
// passenger, grouped by segment, rebuilt live as passengers/segments are
// added or removed via the nested-fields controllers this element wraps.
//
// Inputs can't be tied to real FlightSegmentPassenger records here -
// those don't exist until the flight (and its passengers/segments) are
// actually saved (see Flight#sync_segment_passengers). So cells are
// named by segment/passenger *position* instead, and the controller
// reconciles flight[segment_passenger_matrix][...] back to the real rows
// after save (see Console::FlightsController#apply_segment_passenger_matrix).
export default class extends Controller {
  static targets = ["grid"];

  connect() {
    this.rebuild();
  }

  rebuild() {
    const previousValues = this._captureValues();
    const passengers = this._rows("passenger");
    const segments = this._rows("segment");

    this.gridTarget.innerHTML = segments
      .map((segment) => this._segmentGroup(segment, passengers, previousValues))
      .join("");
  }

  _rows(kind) {
    return Array.from(
      this.element.querySelectorAll(
        `[data-nested-fields-kind="${kind}"] [data-nested-fields-target="item"]:not([hidden])`
      )
    ).map((item, visibleIndex) => {
      const positionField = item.querySelector('input[name*="[position]"]');
      const position = positionField && positionField.value !== "" ? positionField.value : String(visibleIndex);
      return { position };
    });
  }

  _segmentGroup(segment, passengers, previousValues) {
    if (passengers.length === 0) return "";

    const header = `
      <tr class="bg-base-200">
        <td colspan="5" class="text-xs font-semibold text-base-content/60">Segment ${Number(segment.position) + 1}</td>
      </tr>`;
    const rows = passengers.map((passenger) => this._row(segment, passenger, previousValues)).join("");
    return header + rows;
  }

  _row(segment, passenger, previousValues) {
    const key = `${segment.position}:${passenger.position}`;
    const prefix = `flight[segment_passenger_matrix][${segment.position}][${passenger.position}]`;
    const val = (field) => (previousValues[key] && previousValues[key][field]) || "";

    return `
      <tr>
        <td class="text-sm font-medium">Passenger ${Number(passenger.position) + 1}</td>
        <td><input class="input input-sm w-24" name="${prefix}[seat]" value="${val("seat")}"></td>
        <td><input class="input input-sm w-24" name="${prefix}[carry_on_baggage]" value="${val("carry_on_baggage")}" placeholder="e.g. 7kg"></td>
        <td><input class="input input-sm w-24" name="${prefix}[checked_baggage]" value="${val("checked_baggage")}" placeholder="e.g. 2x23kg"></td>
        <td><input class="input input-sm w-full" name="${prefix}[special_service]" value="${val("special_service")}"></td>
      </tr>`;
  }

  _captureValues() {
    const values = {};
    this.gridTarget.querySelectorAll("input[name]").forEach((input) => {
      const match = input.name.match(/segment_passenger_matrix]\[(\d+)]\[(\d+)]\[(\w+)]/);
      if (!match) return;
      const [ , segmentPosition, passengerPosition, field ] = match;
      const key = `${segmentPosition}:${passengerPosition}`;
      values[key] ||= {};
      values[key][field] = input.value;
    });
    return values;
  }
}
